import { useState } from 'react'

import { VatRecordDto } from '../../_generated/api/user-management'
import { useUserContext } from '../contexts/UserContext'
import { useToastContext } from '../contexts/ToastContext'
import FlowCard from '../flow-card/FlowCard'
import FlowCardSideBar from '../flow-card/FlowCardSideBar'
import FlowCardStep, { FlowCardStepProps } from '../flow-card/FlowCardStep'
import { FlowCardStepData } from '../flow-card/FlowCardStepData'
import { SingleContentColumn } from '../common/pure/layout'
import { Column } from '../common/pure/FlexContainer'
import { TextField } from '../common/pure/input-fields'
import ToastOnce from '../common/ToastOnce'
import OrganizationAssociationList from '../common/pure/OrganizationAssociationList'
import Organization from '../../models/Organization'
import { useReferencedNavigate } from '../../hooks/referenced'
import t from '../../tools/t'
import { useIsAdminOrRedirectToIndex } from '../../hooks/admin'

type CreateOrganizationStepType = 'details' | 'association'
interface CreateOrganizationStepData extends FlowCardStepData {
    key: CreateOrganizationStepType
}

const isVATRecordValid = (r: VatRecordDto) => r.name && r.vatNo

export default function CreateOrganizationScreen() {
    const navigate = useReferencedNavigate()
    const { createOrganization, createOrganizationError } = useUserContext()
    const Toast = useToastContext()
    const [name, setName] = useState('')
    const [associations, setAssociations] = useState<VatRecordDto[]>([
        Organization.getEmptyVatRecord(),
    ])
    const [currentStepIndex, setCurrentStepIndex] = useState(0)

    useIsAdminOrRedirectToIndex()

    const steps: CreateOrganizationStepData[] = [
        {
            key: 'details',
            title: t('organization_create_details_step_title'),
            subtitle: t('organization_create_details_step_subtitle'),
            label: t('organization_create_details_step_menu_label'),
        },
        {
            key: 'association',
            title: t('organization_create_association_step_title'),
            subtitle: t('organization_create_association_step_subtitle', name),
            label: t('organization_create_association_step_menu_label'),
        },
    ]

    const currentStep = steps[currentStepIndex]
    const isNameValid = !!name

    const validRecords = associations.filter(isVATRecordValid)
    const isAssociationValid = validRecords.length > 0

    const isValid = (() => {
        switch (currentStep.key) {
            case 'details':
                return isNameValid
            case 'association':
                return isAssociationValid
        }
    })()

    const onStepClick = (index: number) => setCurrentStepIndex(index)
    const onCancel = () => navigate('/organizations')
    const onNext = () => setCurrentStepIndex(currentStepIndex + 1)
    const onCreate = async () => {
        if (!isAssociationValid) return
        const organization = await createOrganization({ name, vatRecords: validRecords })
        if (organization) {
            navigate('/organizations')
            Toast.completed('create', 'organization')
        }
    }

    return (
        <SingleContentColumn columns={3}>
            <FlowCard
                steps={steps.length}
                current={currentStepIndex}
                onCancel={onCancel}
                onNext={onNext}
                onDone={onCreate}
                valid={isValid}
                aside={
                    <FlowCardSideBar
                        title={t('organization_create_flow_title')}
                        icon="address-book"
                        steps={steps}
                        onStepClick={onStepClick}
                        currentStepIndex={currentStepIndex}
                    />
                }
                currentStep={(() => {
                    switch (currentStep.key) {
                        case 'details':
                            return (
                                <CreateOrganizationDetailsStep
                                    title={currentStep.title}
                                    subtitle={currentStep.subtitle}
                                    name={name}
                                    setName={setName}
                                />
                            )

                        case 'association':
                            return (
                                <CreateOrganizationVATStep
                                    title={currentStep.title}
                                    subtitle={currentStep.subtitle}
                                    associations={associations}
                                    setAssociations={setAssociations}
                                />
                            )

                        default:
                            return undefined
                    }
                })()}
            />
            <ToastOnce key="createOrganizationError" error={createOrganizationError} />
        </SingleContentColumn>
    )
}

interface CreateOrganizationDetailsStepProps extends FlowCardStepProps {
    name: string
    setName: (name: string) => void
}
const CreateOrganizationDetailsStep = ({
    name,
    setName,
    ...props
}: CreateOrganizationDetailsStepProps) => {
    return (
        <FlowCardStep {...props}>
            <Column gap={2}>
                <TextField
                    value={name}
                    placeholder={t('organization_field_name')}
                    onChange={(e) => setName(e.target.value)}
                />
            </Column>
        </FlowCardStep>
    )
}

interface CreateOrganizationVATStepProps extends FlowCardStepProps {
    associations: VatRecordDto[]
    setAssociations: (records: VatRecordDto[]) => void
}
const CreateOrganizationVATStep = ({
    associations: records,
    setAssociations: setRecords,
    ...props
}: CreateOrganizationVATStepProps) => {
    return (
        <FlowCardStep {...props}>
            <OrganizationAssociationList associations={records} setAssociations={setRecords} />
        </FlowCardStep>
    )
}
